import React, { useMemo } from 'react';
import {
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonList,
    IonCard,
    IonCardContent,
    IonText,
    IonIcon,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonPopover,
    IonGrid,
    IonRow,
    IonCol,
    IonContent
} from '@ionic/react';
import { warning, alertCircle, informationCircleOutline, trashBinOutline, chevronDown } from 'ionicons/icons';

const PreviewInvoiceStep = ({
    previewInvoices,
    duplicateWarningsMap,
    currency,
    onEditAmount,
    onEditDate,
    onDeleteInvoiceItem,
    onDeleteRow,
    onGenerate,
    searchText,
    onSearchChange,
    propertyOptions,
    selectedPropertyId,
    onPropertyFilterChange,
    onClearFilters
}) => {
    const counts = useMemo(() => {
        const withWarnings = previewInvoices.filter(inv =>
            (inv.items || []).some(item => item.warnings && item.warnings.length > 0)
        ).length;
        const withErrors = previewInvoices.filter(inv =>
            (inv.items || []).some(item => item.errors && item.errors.length > 0)
        ).length;
        return { withWarnings, withErrors, total: previewInvoices.length };
    }, [previewInvoices]);

    const filteredInvoices = useMemo(() => {
        let filtered = [...previewInvoices];

        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase().trim();
            filtered = filtered.filter(inv =>
                inv.tenantName.toLowerCase().includes(searchLower)
            );
        }

        if (selectedPropertyId) {
            filtered = filtered.filter(inv => String(inv.propertyId) === String(selectedPropertyId));
        }

        return filtered;
    }, [previewInvoices, searchText, selectedPropertyId]);

    return (
        <div className="">
            <IonText color="primary" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    Invoice Preview

                    {/* Tooltip trigger */}
                    {(counts.withWarnings > 0 || counts.withErrors > 0) && (
                        <>
                            <IonIcon
                                id="invoice-preview-tooltip"
                                icon={informationCircleOutline}
                                style={{ fontSize: '1.2rem', marginLeft: '0.3rem', cursor: 'pointer' }}
                            />
                            
                            <IonPopover trigger="invoice-preview-tooltip" triggerAction="hover">
                                <IonCard style={{ boxShadow: 'none', margin: 0 }}>
                                    <IonCardContent>

                                        {counts.withErrors > 0 && (
                                            <IonText color="danger">
                                                <p style={{ margin: 0 }}>
                                                    <IonIcon icon={alertCircle} />{" "}
                                                    {counts.withErrors} invoice{counts.withErrors !== 1 ? "s" : ""} with errors
                                                </p>
                                            </IonText>
                                        )}

                                        {counts.withWarnings > 0 && (
                                            <IonText color="warning">
                                                <p style={{ margin: 0 }}>
                                                    <IonIcon icon={warning} />{" "}
                                                    {counts.withWarnings} invoice{counts.withWarnings !== 1 ? "s" : ""} with warnings
                                                </p>
                                            </IonText>
                                        )}

                                    </IonCardContent>
                                </IonCard>
                            </IonPopover>
                        </>
                    )}
                </h2>
            </IonText>

            <IonCard>
                <IonCardContent>
                    <IonSearchbar
                        value={searchText}
                        onIonInput={(e) => onSearchChange(e.detail.value)}
                        placeholder="Search by tenant name..."
                    />

                    <IonItem>
                        <IonLabel position="stacked">Filter by Property</IonLabel>
                        <IonSelect
                            value={selectedPropertyId}
                            onIonChange={(e) => onPropertyFilterChange(e.detail.value)}
                            placeholder="All Properties"
                        >
                            <IonSelectOption value="">All Properties</IonSelectOption>
                            {propertyOptions.map(prop => (
                                <IonSelectOption key={prop.id} value={prop.id}>
                                    {prop.label}
                                </IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonItem>

                    {(searchText || selectedPropertyId) && (
                        <IonButton
                            fill="outline"
                            size="small"
                            onClick={() => {
                                onClearFilters();
                            }}
                        >
                            Clear Filters
                        </IonButton>
                    )}
                </IonCardContent>
            </IonCard>

            {filteredInvoices.length === 0 ? (
                <IonCard>
                    <IonCardContent>
                        <IonText color="medium">
                            <p className="ion-text-center">No invoices to display.</p>
                        </IonText>
                    </IonCardContent>
                </IonCard>
            ) : (
                <IonAccordionGroup>
                    {filteredInvoices.map((inv) => {
                        const hasWarnings = duplicateWarningsMap[String(inv.tenantId)] || 
                            (inv.items || []).some(item => item.warnings && item.warnings.length > 0);
                        const hasErrors = (inv.items || []).some(item => item.errors && item.errors.length > 0);
                        const totalAmount = (inv.items || []).reduce(
                            (sum, item) => sum + parseFloat(item.amount || 0),
                            0
                        ) || inv.amount || 0;

                        return (
                            <IonAccordion key={inv.tenantId} value={inv.tenantId}>
                                <IonItem slot="header">
                                    <IonGrid style={{ padding: 0 }}>
                                        <IonRow>
                                            <IonCol size="8">
                                                <IonLabel>
                                                    <h3 style={{ margin: 0 }}>{inv.tenantName}</h3>
                                                    <p style={{ margin: 0 }}>
                                                        {inv.propertyName} ({inv.unitName})
                                                    </p>
                                                </IonLabel>
                                            </IonCol>

                                            <IonCol
                                                size="4"
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <strong style={{ fontSize: '1rem' }}>
                                                    {currency?.customerCurrency || ''}
                                                    {parseFloat(totalAmount).toFixed(0)}
                                                </strong>

                                                {hasErrors && (
                                                    <IonBadge color="danger" style={{ marginTop: '0.25rem', alignSelf: 'flex-end' }}>
                                                        Error
                                                    </IonBadge>
                                                )}

                                                {hasWarnings && !hasErrors && (
                                                    <IonBadge color="warning" style={{ marginTop: '0.25rem', alignSelf: 'flex-end' }}>
                                                        Warning
                                                    </IonBadge>
                                                )}
                                            </IonCol>
                                        </IonRow>
                                    </IonGrid>
                                </IonItem>

                                <div slot="content">
                                    <IonCard>
                                        <IonCardContent>

                                            <IonItem>
                                                <IonLabel position="stacked">Invoice Date</IonLabel>
                                                <IonInput
                                                    type="date"
                                                    value={inv.invoiceDate}
                                                    onIonInput={(e) => onEditDate(inv.tenantId, e.detail.value)}
                                                    style={{
                                                        '--background': 'var(--ion-color-step-150, #ececec)',
                                                        padding: '8px',
                                                        borderRadius: '8px',
                                                        marginTop: "1em",
                                                        marginBottom: "1em",
                                                    }}
                                                />
                                            </IonItem>

                                            {(inv.items || []).map((item, idx) => {
                                                const itemHasWarnings = item.warnings && item.warnings.length > 0;
                                                const itemHasErrors = item.errors && item.errors.length > 0;
                                                const isRent = item.name.toLowerCase() === 'rent';

                                                return (
                                                    <React.Fragment key={idx}>
                                                        <IonItem>
                                                            <IonLabel style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                                                <span>{item.name}</span>
                                                                {(itemHasWarnings || itemHasErrors) && (
                                                                    <IonIcon
                                                                        icon={itemHasErrors ? alertCircle : warning}
                                                                        color={itemHasErrors ? 'danger' : 'warning'}
                                                                        style={{ fontSize: '1.2rem', marginLeft: '0.25rem' }}
                                                                    />
                                                                )}
                                                            </IonLabel>

                                                            {isRent ? (
                                                                <IonInput
                                                                    type="number"
                                                                    value={item.amount}
                                                                    onIonInput={(e) =>
                                                                        onEditAmount(inv.tenantId, parseFloat(e.detail.value) || 0, idx)
                                                                    }
                                                                    style={{
                                                                        '--background': 'var(--ion-color-step-150, #ececec)',
                                                                        margin: '8px',
                                                                        borderRadius: '8px',
                                                                        marginTop: '1em',
                                                                        marginLeft: '0.5rem',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <IonInput
                                                                    type="number"
                                                                    value={item.amount}
                                                                    disabled={true}
                                                                    style={{
                                                                        '--background': 'var(--ion-color-step-150, #ececec)',
                                                                        margin: '8px',
                                                                        borderRadius: '8px',
                                                                        marginTop: '1em',
                                                                        marginLeft: '0.5rem',
                                                                    }}
                                                                />
                                                            )}

                                                            <IonButton
                                                                fill="clear"
                                                                color="danger"
                                                                slot="end"
                                                                onClick={() => onDeleteInvoiceItem(inv.tenantId, idx)}
                                                            >
                                                                <IonIcon icon={trashBinOutline} />
                                                            </IonButton>
                                                        </IonItem>
                                                        {(itemHasWarnings || itemHasErrors) && (
                                                            <IonItem lines="none" style={{ paddingTop: '0.25rem', paddingBottom: '0.5rem' }}>
                                                                <IonText>
                                                                    {itemHasErrors ? item.errors.join(', ') : item.warnings.join(', ')}
                                                                </IonText>
                                                            </IonItem>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}

                                            <IonItem>
                                                <IonLabel>
                                                    <strong>Total Amount</strong>
                                                </IonLabel>
                                                <IonText>
                                                    <strong>
                                                        {currency?.customerCurrency || ''}
                                                        {parseFloat(totalAmount).toFixed(0)}
                                                    </strong>
                                                </IonText>
                                            </IonItem>

                                            <IonButton
                                                fill="outline"
                                                color="danger"
                                                expand="block"
                                                onClick={() => onDeleteRow(inv.tenantId)}
                                            >
                                                <IonIcon icon={trashBinOutline} slot="start" />
                                                Delete Invoice
                                            </IonButton>

                                        </IonCardContent>
                                    </IonCard>
                                </div>
                            </IonAccordion>
                        );
                    })}
                </IonAccordionGroup>
            )}

            <IonButton
                expand="block"
                className="ion-margin"
                onClick={onGenerate}
                disabled={filteredInvoices.length === 0}
            >
                Generate Invoices
            </IonButton>
        </div>
    );
};

export default PreviewInvoiceStep;

